import { List, ListItem } from "@vinomofo/components";
import { useMemo } from "react";
import {
  RefinementListProvided,
  connectRefinementList,
} from "react-instantsearch-core";

import CurrentFilterItem from "./CurrentFilterItem";
import { FilterGroup } from "./FilterGroup";
import FilterOption from "./FilterOption";
import { trackAddRemoveFilterEvent } from "../../utils/event-tracking";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useLocation } from "@reach/router";

interface RefinementListProps extends RefinementListProvided {
  attribute: string;
  label: string;
  asCurrentFilter?: boolean;
}

export const RefinementList = connectRefinementList<RefinementListProps>(
  ({
    label,
    items,
    refine,
    currentRefinement,
    asCurrentFilter,
    attribute,
  }: RefinementListProps): JSX.Element => {
    const isChecked = (label: string) => currentRefinement.includes(label);
    const LOCATION = useLocation();
    const {
      setCarryOverFilter,
      carryOverFilter,
      searchState,
    } = useInstantSearch();
    const CURRENT_URL_FILTER = LOCATION?.search.replace(/query=[^&]+&?/, "");

    const isDisabled =
      items.length < 1 && items.filter((item) => item.isRefined).length === 0;

    const inBetween = items.filter(
      (i) => i.label.indexOf("Under") < 0 && i.label.indexOf("Over") < 0
    );
    const over = items.filter((i) => i.label.indexOf("Over") > -1);
    const under = items.filter((i) => i.label.indexOf("Under") > -1);
    let final: any = [];

    if (label === "Category") {
      final = items.map((i: any) => {
        if (i.label === "Red") i.sortKey = 1;
        if (i.label === "White") i.sortKey = 2;
        if (i.label === "Mixed") i.sortKey = 3;
        if (i.label === "Sparkling") i.sortKey = 4;
        if (i.label === "Rosé") i.sortKey = 5;
        if (i.label === "Dessert/Fortified") i.sortKey = 6;
        return i;
      });
      final = inBetween.sort((a: any, b: any) =>
        a.sortKey > b.sortKey ? 1 : -1
      );
    } else if (label === "Country") {
      final = items.map((i: any) => {
        i.sortKey = parseInt(i.count);
        return i;
      });
      final = inBetween.sort((a: any, b: any) =>
        a.sortKey > b.sortKey ? -1 : 1
      );
    } else if (label === "Pack size") {
      final = items.map((i: any) => {
        i.sortKey = parseInt(i.label);
        return i;
      });
      final = inBetween.sort((a: any, b: any) =>
        a.sortKey > b.sortKey ? -1 : 1
      );
    } else if (label === "Varietal" || label === "Region") {
      final = items.sort((a, b) => {
        // sort by count
        if (a.count > b.count) return -1;
        if (a.count < b.count) return 1;
        // sort by label
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });
    } else if (label === "Vintage") {
      final = items.sort((a, b) => {
        if (isNaN(Number(a.label)) && isNaN(Number(b.label))) {
          return a.label.localeCompare(b.label);
        } else if (!isNaN(Number(a.label)) && !isNaN(Number(b.label))) {
          return (b.label as never) - (a.label as never);
        } else if (!isNaN(Number(a.label)) && isNaN(Number(b.label))) {
          return -1;
        } else {
          return 1;
        }
      });
    } else {
      final = inBetween.sort((a, b) => (a.label > b.label ? 1 : -1));
    }

    if (over[0]) final.push(over[0]);
    if (under[0]) final.unshift(under[0]);

    if (CURRENT_URL_FILTER) {
      if (carryOverFilter !== CURRENT_URL_FILTER && LOCATION?.search) {
        setCarryOverFilter(CURRENT_URL_FILTER);
      }
    } else setCarryOverFilter(CURRENT_URL_FILTER);

    return useMemo(
      () => (
        <>
          {asCurrentFilter ? (
            final
              .filter((f) => isChecked(f.label) && f.count > 0)
              .map((item, i) => (
                <CurrentFilterItem
                  key={i}
                  label={item.label}
                  count={item.count}
                  onClick={(event) => {
                    event.preventDefault();
                    trackAddRemoveFilterEvent({
                      eventType: isChecked(item.label) ? "remove" : "add",
                      searchState: searchState,
                      filter: {
                        type: attribute,
                        value: item.label,
                      },
                    });
                    refine(item.value);
                  }}
                />
              ))
          ) : (
            <FilterGroup
              label={label}
              initiallyOpened={currentRefinement?.length > 0}
              activeFiltersCount={currentRefinement?.length}
            >
              <List margin="0" padding="0" listStyleType="none">
                {final.map((item, i) => (
                  <ListItem key={`${item.label}-${i}`}>
                    <FilterOption
                      type="checkbox"
                      label={item.label}
                      count={item.count}
                      selected={item.isRefined}
                      onChange={(event: MouseEvent) => {
                        event.preventDefault();
                        trackAddRemoveFilterEvent({
                          eventType: isChecked(item.label) ? "remove" : "add",
                          searchState: searchState,
                          filter: {
                            type: attribute,
                            value: item.label,
                          },
                        });
                        refine(item.value);
                      }}
                      disabled={isDisabled && !isChecked(item.label)}
                    />
                  </ListItem>
                ))}
              </List>
            </FilterGroup>
          )}
        </>
      ),
      [items]
    );
  }
);
